import { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Area } from 'react-easy-crop';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBack, ArrowForward } from '@mui/icons-material';

import { useDictionary } from '@cvt/hooks/useDictionary';

import { TributeCardWizardContext } from '@modules/Tributes/contexts/TributeCardWizardContext';
import { CardImageCropper } from '@modules/Tributes/components/CardImageCropper/CardImageCropper';
import { getImagePreviewFromFileOrUrl } from '@modules/Tributes/helpers/media';


export const EditImageStep: FC = () => {
  const { selectedCardImage, onNextStep, onPreviousStep, onSetImageCropData, imageCropData, tribute } = useContext(TributeCardWizardContext);
  const dictionary = useDictionary();
  const [resolvedImageUrl, setResolvedImageUrl] = useState<string>(imageCropData?.imageUrl || selectedCardImage?.file || '');

  const [_imageCropData, setImageCropData] = useState<Tributes.ImageCropData | null>(imageCropData || { imageUrl: selectedCardImage?.file });

  useEffect(() => {
    let revoked = false;
    let previewUrl: string | undefined;

    const resolveImageUrl = async () => {
      if (!selectedCardImage?.file) {
        setResolvedImageUrl('');
        return;
      }

      const preview = await getImagePreviewFromFileOrUrl(selectedCardImage.file);
      previewUrl = preview;
      setResolvedImageUrl(preview);
      setImageCropData((prev) => ({ ...(prev || {}), imageUrl: preview }));
    };

    resolveImageUrl();

    return () => {
      if (previewUrl?.startsWith('blob:') && !revoked) {
        URL.revokeObjectURL(previewUrl);
        revoked = true;
      }
    };
  }, [selectedCardImage?.file]);

  const onZoomChange = useCallback((zoom: number) => {
    setImageCropData((prev) => prev ? { ...prev, zoom } : null);
  }, []);

  const onCropChange = useCallback((crop: { x: number; y: number }) => {
    setImageCropData((prev) => prev ? { ...prev, crop } : null);
  }, []);

  const onImageCropAreaPixelsChange = useCallback((pixels: Area) => {
    setImageCropData((prev) => prev ? { ...prev, pixels } : null);
  }, []);

  const onChangeStep = useCallback((stepChangeHandler: () => void) => {
    if (_imageCropData) {
      onSetImageCropData(_imageCropData);
    }
    stepChangeHandler();
  }, [_imageCropData, onSetImageCropData]);

  return (
    <Box>
      <Stack gap={1} alignItems="center" mt={5} mb={4}>
        <Typography variant="h3" fontSize={18} fontWeight={600}>{dictionary.tributes.edit.card.cardDialog.editCard}</Typography>
        <Typography variant="h6" color="text.secondary" fontSize={16}>
          {dictionary.tributes.edit.card.cardDialog.editCardSubtitle}
        </Typography>
      </Stack>
      <Stack alignItems="center" justifyContent="center" mt={2}>
        <CardImageCropper
          petName={tribute?.pet.name}
          onImageCropAreaPixelsChange={onImageCropAreaPixelsChange}
          onImageZoomChange={onZoomChange}
          onImageCropChange={onCropChange}
          imageUrl={resolvedImageUrl}
          defaultValue={imageCropData}
        />
      </Stack>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={4}
      >
        <Button
          variant="outlined"
          color="default"
          onClick={() => onChangeStep(onPreviousStep)}
          sx={{
            px: 3,
            py: 1,
          }}
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => onChangeStep(onNextStep)}
          sx={{
            px: 3,
            py: 1,
          }}
          endIcon={<ArrowForward />}
        >
          Next
        </Button>
      </Stack>
    </Box>
  );
};
